import {
  Activity,
  BookOpen,
  FolderKanban,
  Gauge,
  House,
  KeyRound,
  Rocket,
  ShieldCheck,
  Sparkles,
  User,
  Users,
  UsersRound,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useHealth, useLogout, useVersion } from "@/lib/api";
import { logger } from "@/lib/logger";
import { UserProvider, useUser } from "@/lib/user-context";

const HEALTH_POLL_INTERVAL_MS = 45_000;

/**
 * Polls the backend health endpoint and toasts on connectivity transitions:
 * an error toast when the backend becomes unreachable, a success toast when
 * the connection recovers. Renders nothing.
 */
function BackendHealthToaster() {
  const { isError } = useHealth(true, HEALTH_POLL_INTERVAL_MS);
  const wasError = useRef(false);

  useEffect(() => {
    if (isError && !wasError.current) {
      logger.debug("[health]", "backend became unreachable");
      toast.error("Backend unreachable — some features may not work.");
    } else if (!isError && wasError.current) {
      logger.debug("[health]", "backend connection restored");
      toast.success("Backend connection restored.");
    }
    wasError.current = isError;
  }, [isError]);

  return null;
}

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const GENERAL_NAV: NavItem[] = [
  { title: "Home", href: "/home", icon: House },
  { title: "Get Started", href: "/how-to", icon: BookOpen },
  { title: "You", href: "/you", icon: User },
];

const ADMIN_NAV: NavItem[] = [
  { title: "Users", href: "/admin/users", icon: Users },
  { title: "Groups", href: "/admin/groups", icon: UsersRound },
  { title: "Projects", href: "/admin/projects", icon: FolderKanban },
  { title: "Activity", href: "/admin/activity", icon: Activity },
  { title: "App Registrations", href: "/admin/app-registrations", icon: KeyRound },
  { title: "IT Approvals", href: "/admin/it-approvals", icon: ShieldCheck },
  { title: "Promotion Requests", href: "/admin/promotion-requests", icon: Rocket },
];

// Only on an instance that gives projects services gateway keys (`svc_gateway_enabled`).
const SERVICES_GATEWAY_NAV: NavItem[] = [
  { title: "AI Services", href: "/admin/ai-services", icon: Sparkles },
  { title: "Budget Requests", href: "/admin/service-budget-requests", icon: Gauge },
];

function NavGroup({ label, items }: { label: string; items: NavItem[] }) {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="tracking-wider uppercase">{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                  <Link
                    to={item.href}
                    onClick={() => {
                      if (isMobile) {
                        setOpenMobile(false);
                      }
                    }}
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function AppSidebar() {
  const { user } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: version } = useVersion(isAdmin);
  const adminNav = version?.svc_gateway_enabled
    ? [...ADMIN_NAV, ...SERVICES_GATEWAY_NAV]
    : ADMIN_NAV;

  return (
    <Sidebar>
      <SidebarHeader className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Link to="/home" className="flex items-center">
            <img src="/brand/norican-logo-white.svg" alt="Norican" className="h-6 w-auto" />
          </Link>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <NavGroup label="General" items={GENERAL_NAV} />
        {isAdmin && <NavGroup label="Admin" items={adminNav} />}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}

function TopBar() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { user } = useUser();

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  const greeting = user?.first_name ? `Welcome, ${user.first_name}` : "Welcome";

  return (
    <header className="border-border flex items-center justify-between border-b px-4 py-3">
      <SidebarTrigger />
      <div className="flex items-center gap-4">
        <span className="text-muted-foreground text-sm">{greeting}</span>
        <Button variant="outline" onClick={handleLogout}>
          Sign out
        </Button>
      </div>
    </header>
  );
}

/**
 * Shell for all authenticated routes: provides the UserProvider context, the
 * left navigation sidebar, a slim topbar (sidebar trigger + greeting + sign
 * out), and renders the matched child route via <Outlet />.
 */
export function AppLayout() {
  return (
    <UserProvider>
      <SidebarProvider>
        <BackendHealthToaster />
        <AppSidebar />
        <SidebarInset>
          <TopBar />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </UserProvider>
  );
}
